import { ArrowDropDown, ArrowDropUp, Search, Sort } from '@mui/icons-material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import {
  Box,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
} from '@mui/material';
import React, { useMemo, useState } from 'react';
import { IDataGridColumn } from '../interfaces/IDataGridColumn';
import ExpandableTableCell from './ExpandableTableCell';

interface IKeyValMap {
  [key: string]: any;
}

type TColumnsResolver = (
  col: IDataGridColumn,
  row: IKeyValMap,
) => React.ReactNode | undefined;

interface DataTableProps {
  rows: IKeyValMap[];
  columns: IDataGridColumn[];
  columnsResolver?: TColumnsResolver;
  expandableRows?: boolean;
  defaultSortDirection?: 'asc' | 'desc';
  defaultSortKey?: string | null;
  rowIdField?: string;
}

export const DataTable: React.FC<DataTableProps> = ({
  rows,
  columns,
  columnsResolver,
  expandableRows = false,
  defaultSortDirection = 'desc',
  defaultSortKey = null,
  rowIdField = 'id',
}) => {
  const [sortConfig, setSortConfig] = useState<{
    key: string | null;
    direction: 'asc' | 'desc';
  }>({ key: defaultSortKey, direction: defaultSortDirection });
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const [rowColumnExceedingHeight, setRowColumnExceedingHeight] = useState<
    Map<string, Set<number>>
  >(new Map());

  const sortedRows = useMemo(() => {
    if (!sortConfig.key) return rows;

    const column = columns.find(col => col.name === sortConfig.key);
    if (!column) return rows;

    return [...rows].sort((a, b) => {
      const aVal = a[sortConfig.key!];
      const bVal = b[sortConfig.key!];

      if (aVal == null) return 1;
      if (bVal == null) return -1;

      if (column.sortFn) {
        return sortConfig.direction === 'asc'
          ? column.sortFn(aVal, bVal)
          : column.sortFn(bVal, aVal);
      }

      const comparison =
        typeof aVal === 'string'
          ? aVal.localeCompare(bVal)
          : aVal < bVal
            ? -1
            : aVal > bVal
              ? 1
              : 0;

      return sortConfig.direction === 'asc' ? comparison : -comparison;
    });
  }, [rows, sortConfig, columns]);

  const handleSort = (columnName: string) => {
    setSortConfig(current => ({
      key: columnName,
      direction:
        current.key === columnName && current.direction === 'asc'
          ? 'desc'
          : 'asc',
    }));
  };

  const handleExpandClick = (rowId: string) => {
    setExpandedRows(prev => {
      const updated = new Set(prev);
      updated.has(rowId) ? updated.delete(rowId) : updated.add(rowId);
      return updated;
    });
  };

  const handleContentExceedsHeight = (
    rowId: string,
    colIndex: number,
    exceeds: boolean,
  ) => {
    setRowColumnExceedingHeight(prev => {
      const updated = new Map(prev);
      const columnSet = updated.get(rowId) || new Set();

      if (exceeds) {
        columnSet.add(colIndex);
      } else {
        columnSet.delete(colIndex);
      }

      if (columnSet.size === 0) {
        updated.delete(rowId);
      } else {
        updated.set(rowId, columnSet);
      }

      return updated;
    });
  };

  const doesRowExceedHeight = (rowId: string) => {
    return rowColumnExceedingHeight.has(rowId);
  };

  const renderCellContent = (col: IDataGridColumn, row: IKeyValMap) => {
    const resolvedValue = columnsResolver?.(col, row);
    return resolvedValue !== undefined ? resolvedValue : row[col.name];
  };

  const getRowId = (row: IKeyValMap): string => {
    if (row[rowIdField] !== undefined) {
      return String(row[rowIdField]);
    }
    return Object.values(row)
      .map(val => String(val))
      .join('_');
  };

  return (
    <Box sx={{ display: 'flex' }}>
      <TableContainer
        component={Paper}
        sx={{ flex: '1 1 auto', overflowX: 'auto' }}
      >
        <Table
          stickyHeader
          sx={{
            minWidth: {
              xs: `calc(${columns.length} * 150px)`,
              xl: `calc(${columns.length} * 7vw)`,
            },
          }}
        >
          <TableHead>
            <TableRow>
              {columns.map(col => (
                <TableCell
                  key={col.name}
                  sx={{
                    minWidth: col.flexBasis || '150px',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {col.sortable ? (
                    <TableSortLabel
                      active={sortConfig.key === col.name}
                      direction={sortConfig.direction}
                      onClick={() => handleSort(col.name)}
                      IconComponent={() => (
                        <SortIcon
                          direction={sortConfig.direction}
                          active={sortConfig.key === col.name}
                        />
                      )}
                    >
                      {col.labelCopy?.toUpperCase() || col.name.toUpperCase()}
                    </TableSortLabel>
                  ) : (
                    col.labelCopy?.toUpperCase() || col.name.toUpperCase()
                  )}
                </TableCell>
              ))}
              {expandableRows && <TableCell />}
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedRows.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={expandableRows ? columns.length + 1 : columns.length}
                  align="center"
                  sx={{ py: 8 }}
                >
                  <Stack
                    spacing={1}
                    alignItems="center"
                    sx={{ color: 'text.secondary' }}
                  >
                    <Search sx={{ fontSize: '2rem', opacity: 0.7 }} />
                    No data found.
                  </Stack>
                </TableCell>
              </TableRow>
            ) : (
              sortedRows.map(row => {
                const rowId = getRowId(row);
                return (
                  <TableRow
                    key={rowId}
                    hover={!expandableRows}
                    sx={{ position: 'relative' }}
                  >
                    {!expandableRows &&
                      columns.map((col, colIndex) => (
                        <TableCell key={colIndex}>
                          {renderCellContent(col, row)}
                        </TableCell>
                      ))}

                    {expandableRows &&
                      columns.map((col, colIndex) => (
                        <ExpandableTableCell
                          key={colIndex}
                          collapsedHeight={200}
                          isExpanded={expandedRows.has(rowId)}
                          onContentExceedsHeight={exceeds =>
                            handleContentExceedsHeight(rowId, colIndex, exceeds)
                          }
                        >
                          {renderCellContent(col, row)}
                        </ExpandableTableCell>
                      ))}

                    {expandableRows && (
                      <TableCell>
                        {doesRowExceedHeight(rowId) && (
                          <IconButton
                            size="small"
                            aria-label="expand row"
                            onClick={() => handleExpandClick(rowId)}
                          >
                            {expandedRows.has(rowId) ? (
                              <KeyboardArrowUpIcon />
                            ) : (
                              <KeyboardArrowDownIcon />
                            )}
                          </IconButton>
                        )}
                      </TableCell>
                    )}

                    {expandableRows &&
                      doesRowExceedHeight(rowId) &&
                      !expandedRows.has(rowId) && (
                        <Box
                          sx={{
                            position: 'absolute',
                            bottom: 0,
                            left: 0,
                            right: 0,
                            height: '100px',
                            background:
                              'linear-gradient(to bottom, transparent, white)',
                            pointerEvents: 'none',
                          }}
                        />
                      )}
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

const SortIcon: React.FC<{
  direction: 'asc' | 'desc';
  active: boolean;
}> = ({ direction, active }) => {
  if (!active) {
    return <Sort style={{ marginLeft: '8px', opacity: 0.5 }} />;
  }

  switch (direction) {
    case 'asc':
      return (
        <ArrowDropUp style={{ marginLeft: '8px', opacity: active ? 1 : 0.5 }} />
      );
    case 'desc':
      return <ArrowDropDown style={{ marginLeft: '8px', opacity: 1 }} />;
    default:
      return <Sort style={{ marginLeft: '8px', opacity: 0.5 }} />;
  }
};
